// Pure helpers for the Device Schedule page. Everything here is driven by the
// field config in constants.js - no function below knows a section or a field
// by name, so a new field in the config renders, validates and saves on its own.
import { SCHEDULE_SECTIONS } from "./constants";

const DAY_MS = 86400000;

const two = (value) => String(value).padStart(2, "0");

// The service has been seen storing `active` as "", so it is read as a boolean
// rather than compared against true.
export const toBool = (value) => {
  if (typeof value === "string") {
    const text = value.trim().toLowerCase();
    return !(text === "" || text === "false" || text === "0");
  }
  return Boolean(value);
};

// Schedule values are flat numbers. A missing or unparseable one reads as 0,
// which for a time is a real midnight and for a date part means "unset".
const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// ---------------------------------------------------------------- config reads

export const editableFields = (section) =>
  section.fields.filter((field) => field.type !== "timestamp");

// What the table shows as chips: `active` is carried by the status pill and the
// timestamp has its own line, so neither repeats as a chip.
export const chipFields = (section) =>
  section.fields.filter((field) => field.type === "time" || field.type === "date");

export const activeField = (section) =>
  section.fields.find((field) => field.type === "boolean");

export const timestampField = (section) =>
  section.fields.find((field) => field.type === "timestamp");

// The raw keys one control is built from.
export const fieldKeys = (field) => {
  if (field.type === "boolean") return [field.activeKey];
  if (field.type === "time") return [field.hourKey, field.minuteKey];
  if (field.type === "date") return [field.dayKey, field.monthKey, field.yearKey];
  return [];
};

// Every key this page writes. The timestamp is device-owned and excluded, so it
// never takes part in a pending comparison and is never sent.
export const writtenKeys = (section) =>
  editableFields(section).flatMap(fieldKeys);

export const sectionByKey = (key) =>
  SCHEDULE_SECTIONS.find((section) => section.key === key) || null;

// --------------------------------------------------------------------- devices

// Every EnergiSync device in the house, flattened out of its areas. Deduplicated
// on deviceId: the same device can be listed under more than one area, and two
// rows editing one device would be two views of the same schedule.
export const collectDevices = (house) => {
  const seen = new Map();

  for (let room of house?.rooms ?? []) {
    for (let device of room.energiSync ?? []) {
      if (!device?.deviceId || seen.has(device.deviceId)) continue;

      seen.set(device.deviceId, {
        deviceId: device.deviceId,
        name: device.name || device.assetName || "",
        roomName: room.roomName || "",
        device,
      });
    }
  }

  return [...seen.values()];
};

// Device Master's "To be scheduled" flag, from the asset's scheduleConfig.
// Only an explicit yes counts: a device with no flag is not listed here.
export const isToBeScheduled = (device) => {
  const value = device?.scheduleConfig?.toBeScheduled;
  return value === true || /^(true|yes)$/i.test(String(value ?? "").trim());
};

// The devices this page lists: those set to be scheduled.
export const scheduledDevices = (house) =>
  collectDevices(house).filter((entry) => isToBeScheduled(entry.device));

// A device can carry no schedule object at all, which is not the same as one
// full of zeroes - the editor starts empty rather than at midnight.
export const scheduleOf = (device, key) => {
  const raw = device?.[key];
  return raw && typeof raw === "object" ? raw : null;
};

// ---------------------------------------------------------- raw <-> form values

// Times are "HH:MM" and dates "YYYY-MM-DD", so each control is one input and one
// form value however many raw keys sit behind it.
export const rawToForm = (raw, section) => {
  const form = {};

  for (let field of editableFields(section)) {
    if (field.type === "boolean") {
      form[field.name] = raw ? toBool(raw[field.activeKey]) : false;
      continue;
    }

    if (field.type === "time") {
      form[field.name] = raw
        ? `${two(num(raw[field.hourKey]))}:${two(num(raw[field.minuteKey]))}`
        : "";
      continue;
    }

    if (field.type === "date") {
      const day = num(raw?.[field.dayKey]);
      const month = num(raw?.[field.monthKey]);
      const year = num(raw?.[field.yearKey]);

      // A zeroed date part is unset, unlike a zeroed time.
      form[field.name] =
        day && month && year ? `${year}-${two(month)}-${two(day)}` : "";
    }
  }

  return form;
};

const splitTime = (value) => {
  const [hour, minute] = String(value || "").split(":");
  return { hour: num(hour), minute: num(minute) };
};

const splitDate = (value) => {
  const [year, month, day] = String(value || "").split("-");
  return { day: num(day), month: num(month), year: num(year) };
};

// `base` is the object that arrived from the service, so keys this page does not
// own survive the round trip. A cleared control writes 0 rather than dropping
// its key, which keeps the schedule object the shape the device sent.
export const formToRaw = (form, section, base) => {
  const raw = { ...(base || {}) };

  const stamp = timestampField(section);
  if (stamp) delete raw[stamp.timestampKey];

  for (let field of editableFields(section)) {
    if (field.type === "boolean") {
      raw[field.activeKey] = Boolean(form[field.name]);
      continue;
    }

    if (field.type === "time") {
      const { hour, minute } = splitTime(form[field.name]);
      raw[field.hourKey] = hour;
      raw[field.minuteKey] = minute;
      continue;
    }

    if (field.type === "date") {
      const { day, month, year } = splitDate(form[field.name]);
      raw[field.dayKey] = day;
      raw[field.monthKey] = month;
      raw[field.yearKey] = year;
    }
  }

  return raw;
};

// ------------------------------------------------------- current / desired / pending

// What the cloud is asking for: the device's own values with the expected ones
// laid over them, so the editor opens on the queued change rather than hiding it.
export const mergeExpected = (current, expected, section) => {
  if (!expected) return current;

  const merged = { ...(current || {}) };

  for (let key of writtenKeys(section)) {
    if (Object.prototype.hasOwnProperty.call(expected, key)) {
      merged[key] = expected[key];
    }
  }

  return merged;
};

// Pending means the cloud holds a value the device has not applied yet. Compared
// per field, so booleans compare as booleans and numbers as numbers.
export const isPending = (current, expected, section) => {
  if (!expected) return false;

  return editableFields(section).some((field) =>
    fieldKeys(field).some((key) => {
      if (!Object.prototype.hasOwnProperty.call(expected, key)) return false;

      return field.type === "boolean"
        ? toBool(expected[key]) !== toBool(current?.[key])
        : num(expected[key]) !== num(current?.[key]);
    })
  );
};

// What a section's cell compares: what the device reports, and what the cloud
// is asking for - the device document's expected schedule with a just-sent
// override laid on top. The table and the automatic re-check both use this, so
// they agree on what "pending" means.
export const sectionSides = (device, section, override) => {
  const current = scheduleOf(device, section.readKey);
  const reported = scheduleOf(device, section.writeKey);
  const expected = reported || override ? { ...(reported || {}), ...(override || {}) } : null;
  return { current, expected };
};

// Whether the device is connected, from its own document: it records when it
// last connected and last disconnected, and the later of the two is its state.
// Matches the live /connectionStatus answer (connectedAt is its connectedSince)
// without a call of its own. `online` is null when the document has neither -
// the row then shows no status rather than a guess. `since` is when it last
// connected or disconnected.
export const connectionOf = (device) => {
  const connectedAt = Number(device?.connectedAt) || 0;
  const disconnectedAt = Number(device?.disconnectedAt) || 0;
  if (!connectedAt && !disconnectedAt) return { online: null, since: null };
  const online = connectedAt > disconnectedAt;
  return { online, since: online ? connectedAt : disconnectedAt };
};

// The same shape from a live /connectionStatus answer, which says whether the
// device is connected now and, if so, since when. Offline, the answer has no
// time, so the device document's last disconnect is used when it is the later
// event (the device went down after it last came up).
export const liveConnectionOf = (status, device) => {
  if (status.connected) {
    return { online: true, since: status.connectedSince || Number(device?.connectedAt) || null };
  }
  const connectedAt = Number(device?.connectedAt) || 0;
  const disconnectedAt = Number(device?.disconnectedAt) || 0;
  return { online: false, since: disconnectedAt > connectedAt ? disconnectedAt : null };
};

// When a device connected or disconnected, as the page shows it: "27 Oct, 10:02 am".
export const formatSince = (timestamp) =>
  new Date(timestamp).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });

// The value each header filter matches on, as the table shows it: the device
// id and name, Online / Offline (blank when unknown), and per section the cell's
// pill - or "Pending" when a change is waiting, since that is what a filter on
// it is usually after.
export const SYNC_LABEL = { active: "Active", inactive: "Inactive", unset: "Not set" };
export const filterValues = (row) => {
  const values = {
    deviceId: row.deviceId,
    name: row.name || "",
    connection:
      row.connection?.online === true ? "Online" : row.connection?.online === false ? "Offline" : "",
  };
  for (const section of SCHEDULE_SECTIONS) {
    const cell = row.cells?.[section.key];
    values[`sync:${section.key}`] = cell?.pending ? "Pending" : SYNC_LABEL[cell?.status] || "";
  }
  return values;
};

// True while any of the device's sections has a change it has not applied.
// `overrides` is that device's sectionKey -> last-sent schedule.
export const devicePending = (device, overrides = {}) =>
  SCHEDULE_SECTIONS.some((section) => {
    const { current, expected } = sectionSides(device, section, overrides[section.key]);
    return isPending(current, expected, section);
  });

// -------------------------------------------------------------------- validation

const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const dayOffset = (value) => {
  const { day, month, year } = splitDate(value);
  const picked = new Date(year, month - 1, day);
  picked.setHours(0, 0, 0, 0);
  return Math.round((picked - startOfToday()) / DAY_MS);
};

const isoDate = (offsetDays) => {
  const date = startOfToday();
  date.setDate(date.getDate() + offsetDays);
  return `${date.getFullYear()}-${two(date.getMonth() + 1)}-${two(date.getDate())}`;
};

// Feeds the input's own min/max, so the picker refuses out-of-range dates before
// the validator has to.
export const dateBounds = (field) => ({
  min: field.minOffsetDays === undefined ? undefined : isoDate(field.minOffsetDays),
  max: field.maxOffsetDays === undefined ? undefined : isoDate(field.maxOffsetDays),
});

// Blocking, keyed by field name. `initial` is what the editor opened on, which is
// what makes "only the fields the user changed" possible.
//
// ON and OFF are deliberately not ordered against each other: a schedule may
// legitimately run past midnight.
export const validateForm = (form, section, initial) => {
  const errors = {};

  for (let field of editableFields(section)) {
    // A boolean always has a value, and false is one of them.
    if (field.type === "boolean") continue;

    const value = form[field.name];

    // A half-filled schedule is not something the device can act on.
    if (!value) {
      errors[field.name] = "Required";
      continue;
    }

    if (field.type !== "date") continue;

    // Bounds are enforced only on a field the user actually moved, so a device
    // holding an expired window can still be saved - and switched off - without
    // being re-dated first.
    if (value !== initial?.[field.name]) {
      const offset = dayOffset(value);

      if (field.minOffsetDays !== undefined && offset < field.minOffsetDays) {
        errors[field.name] =
          field.minOffsetDays === 0
            ? "Cannot be before today"
            : `Must be at least ${field.minOffsetDays} days from today`;
        continue;
      }

      if (field.maxOffsetDays !== undefined && offset > field.maxOffsetDays) {
        errors[field.name] = `Cannot be more than ${field.maxOffsetDays} days from today`;
        continue;
      }
    }

    if (!field.after) continue;

    const other = form[field.after.field];
    if (!other) continue;

    // Both sides are YYYY-MM-DD, which sorts chronologically as a string.
    const tooEarly = field.after.inclusive ? value < other : value <= other;
    if (tooEarly) errors[field.name] = field.after.message;
  }

  return errors;
};

export const isDirty = (form, initial) =>
  Object.keys(form).some((name) => form[name] !== initial?.[name]);

// -------------------------------------------------------------------- formatting

// Returns null when there is nothing to show, so callers can render their own
// placeholder rather than the string "0" or "undefined".
export const formatField = (field, raw) => {
  if (!raw) return null;

  if (field.type === "time") {
    return `${two(num(raw[field.hourKey]))}:${two(num(raw[field.minuteKey]))}`;
  }

  if (field.type === "date") {
    const day = num(raw[field.dayKey]);
    const month = num(raw[field.monthKey]);
    const year = num(raw[field.yearKey]);
    return day && month && year ? `${two(day)}/${two(month)}/${year}` : null;
  }

  if (field.type === "timestamp") {
    const stamp = num(raw[field.timestampKey]);
    return stamp ? new Date(stamp).toLocaleString() : null;
  }

  return null;
};

export const statusOf = (raw, section) => {
  if (!raw) return "unset";
  const field = activeField(section);
  return field && toBool(raw[field.activeKey]) ? "active" : "inactive";
};

export const matchesSearch = (row, query) => {
  const text = query.trim().toLowerCase();
  if (!text) return true;

  return [row.deviceId, row.name, row.roomName]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(text));
};
